import type {
  MultilineElementTransformer,
  Transformer,
} from '@lexical/markdown';
import {
  $generateNodesFromMarkdownString,
  TEXT_FORMAT_TRANSFORMERS,
  TEXT_MATCH_TRANSFORMERS,
} from '@lexical/markdown';
import {
  $createTableCellNode,
  $createTableNode,
  $createTableRowNode,
  $isTableNode,
  $isTableRowNode,
  TableCellHeaderStates,
  TableCellNode,
  TableNode,
  TableRowNode,
} from '@lexical/table';
import type { ElementNode } from 'lexical';
import { $createParagraphNode, $isElementNode } from 'lexical';

/**
 * `@lexical/markdown` ships no table transformer — its only table-aware code
 * (`isTableRowDivider`, `TABLE_ROW_REG_EXP`) exists to stop pipe-delimited lines
 * being merged into paragraphs. Without this transformer, GFM tables import as
 * one paragraph per line of literal text.
 *
 * Known limitation: column alignment (`|:--|--:|`) is parsed but not preserved.
 * Alignment is re-exported as `---` for every column because the toolbar offers
 * no way to set it. Tables authored here always round-trip; a pasted aligned
 * table loses its alignment markers.
 */

/** Cell contents are inline-only, so block transformers are deliberately excluded. */
const INLINE_CELL_TRANSFORMERS: Transformer[] = [
  ...TEXT_FORMAT_TRANSFORMERS,
  ...TEXT_MATCH_TRANSFORMERS,
];

const TABLE_ROW_REG_EXP = /^[\t ]*\|(.+)\|[\t ]*$/;
const DIVIDER_CELL_REG_EXP = /^:?-+:?$/;

/**
 * Splits `| a | b |` into `['a', 'b']`, honouring `\|` as an escaped literal
 * pipe rather than a cell boundary.
 */
const splitRowIntoCells = (line: string) =>
  line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split(/(?<!\\)\|/)
    .map(cell => cell.trim().replaceAll(String.raw`\|`, '|'));

const isTableRow = (line: string | undefined) =>
  line !== undefined && TABLE_ROW_REG_EXP.test(line);

/** A GFM table is only a table if line two is the `| --- | --- |` divider. */
const isDividerRow = (line: string | undefined) => {
  if (!isTableRow(line)) return false;

  const cells = splitRowIntoCells(line as string);
  return (
    cells.length > 0 && cells.every(cell => DIVIDER_CELL_REG_EXP.test(cell))
  );
};

const $createCell = ({
  text,
  isHeader,
}: {
  text: string;
  isHeader: boolean;
}) => {
  const cell = $createTableCellNode(
    isHeader ? TableCellHeaderStates.ROW : TableCellHeaderStates.NO_STATUS
  );
  const paragraph = $createParagraphNode();

  // Reuse Lexical's inline parsing so bold/italic/links inside cells behave.
  const generated = $generateNodesFromMarkdownString(
    text,
    INLINE_CELL_TRANSFORMERS,
    false
  );
  generated.forEach(node => {
    if ($isElementNode(node)) {
      node.getChildren().forEach(child => paragraph.append(child));
    } else {
      paragraph.append(node);
    }
  });

  cell.append(paragraph);
  return cell;
};

const $createRow = ({
  cells,
  columnCount,
  isHeader,
}: {
  cells: string[];
  columnCount: number;
  isHeader: boolean;
}) => {
  const row = $createTableRowNode();

  // GFM permits ragged rows; normalise every row to the header's width.
  Array.from({ length: columnCount }).forEach((_unused, index) => {
    row.append($createCell({ text: cells[index] ?? '', isHeader }));
  });

  return row;
};

/**
 * Renders one exported cell. Newlines cannot survive inside a GFM cell, so any
 * block structure collapses to spaces, and literal pipes are escaped.
 */
const exportCell = ({
  cell,
  traverseChildren,
}: {
  cell: TableCellNode;
  traverseChildren: (node: ElementNode) => string;
}) =>
  traverseChildren(cell)
    .replaceAll(/\s*\n+\s*/g, ' ')
    .replaceAll('|', String.raw`\|`)
    .trim();

export const TextareaTableTransformer: MultilineElementTransformer = {
  dependencies: [TableNode, TableRowNode, TableCellNode],
  type: 'multiline-element',
  regExpStart: TABLE_ROW_REG_EXP,
  /**
   * `/$^/` can never match — end-of-input followed by start-of-input. Paired with
   * `optional: true` it means "this block has no closing marker", which is true
   * of a table: it simply ends at the first line that is not a row.
   * `handleImportAfterStartMatch` below decides where that is.
   */
  regExpEnd: { optional: true, regExp: /$^/ },

  handleImportAfterStartMatch: ({ lines, rootNode, startLineIndex }) => {
    const headerLine = lines[startLineIndex];

    if (!isDividerRow(lines[startLineIndex + 1])) {
      // Pipes without a divider are ordinary text — let other transformers run.
      return null;
    }

    const headerCells = splitRowIntoCells(headerLine as string);
    const columnCount = headerCells.length;
    const table = $createTableNode();

    table.append(
      $createRow({ cells: headerCells, columnCount, isHeader: true })
    );

    // Body rows run until the first line that is not a table row.
    let lineIndex = startLineIndex + 2;
    while (isTableRow(lines[lineIndex])) {
      table.append(
        $createRow({
          cells: splitRowIntoCells(lines[lineIndex] as string),
          columnCount,
          isHeader: false,
        })
      );
      lineIndex += 1;
    }

    rootNode.append(table);

    // Second element is the index of the last line consumed.
    return [true, lineIndex - 1];
  },

  /**
   * Required by `MultilineElementTransformer`, but never called here. Lexical
   * only reaches `replace` when `handleImportAfterStartMatch` returns undefined
   * to ask for the default matching behaviour, and the one above always returns
   * either a tuple (handled) or null (try the next transformer). Returning false
   * declines the transform, which is the safe answer if that ever changes.
   *
   * Note this is an import-only path — `@lexical/markdown` documents a multiline
   * transformer's `replace` as "called only when markdown is imported in the
   * editor, not when it's typed". So typing pipe syntax leaving plain text is not
   * this returning false; multiline transformers take no part in typing at all,
   * and a typed table stays plain text until the value is reloaded.
   */
  replace: () => false,

  export: (node, traverseChildren) => {
    if (!$isTableNode(node)) return null;

    const rows = node.getChildren().filter(child => $isTableRowNode(child));
    if (rows.length === 0) return null;

    const renderRow = (row: TableRowNode) =>
      `| ${row
        .getChildren()
        .filter((cell): cell is TableCellNode => cell instanceof TableCellNode)
        .map(cell => exportCell({ cell, traverseChildren }))
        .join(' | ')} |`;

    const headerRow = rows[0] as TableRowNode;
    const columnCount = headerRow.getChildren().length;
    const divider = `| ${Array.from({ length: columnCount })
      .map(() => '---')
      .join(' | ')} |`;

    return [
      renderRow(headerRow),
      divider,
      ...rows.slice(1).map(row => renderRow(row)),
    ].join('\n');
  },
};
